import {schema} from 'normalizr';
import {
  AccessProfile,
  AppUser,
  Area,
  Building,
  Connection,
  ConnectionUser,
  DeskSuggestion,
  Floor,
  LocationProfile,
  Meeting,
  MeetingRoom,
  MeetingSuggestion,
  ParkingLot,
  ParkingZone,
  SuggestedDesk,
  ParkingQuotaProfile,
  TimeZone,
  MeetingRoomSuggestion,
} from './entities';

export const areaEntity = new schema.Entity<Area>(
  'areas',
  {},
  {
    processStrategy: (entity: Area, parent: Floor) => ({
      ...entity,
      parentId: parent.id,
      floorId: parent.id,
      buildingId: parent.buildingId,
      type: 'area',
    }),
  },
);

export const floorEntity = new schema.Entity<Floor>(
  'floors',
  {areas: [areaEntity]},
  {
    processStrategy: (entity, parent) => ({
      ...entity,
      parentId: parent.id,
      buildingId: parent.id,
    }),
  },
);

export const buildingEntity = new schema.Entity<Building>(
  'buildings',
  {
    floors: [floorEntity],
  },
  {
    processStrategy: (entity) => {
      const {ianaTimezone, ...rest} = entity;
      return {
        ...rest,
        timeZoneId: ianaTimezone,
      };
    },
  },
);

export const connectionUserEntity = new schema.Entity<ConnectionUser>(
  'connectionUsers',
  {},
  {
    processStrategy: (entity: ConnectionUser, parent: Connection) => ({
      ...entity,
      connectionId: parent && parent.id,
      isConnecting: false,
    }),
  },
);

export const connectionEntity = new schema.Entity<Connection>(
  'connections',
  {connectedUser: connectionUserEntity},
  {
    processStrategy: (entity: Connection) => ({
      ...entity,
      isAccepting: false,
      isIgnoring: false,
      isDeleting: false,
    }),
  },
);

export const suggestedDeskEntity = new schema.Entity<SuggestedDesk>(
  'suggestedDesks',
  {},
  {
    processStrategy: (entity, parent) => ({
      ...entity,
      deskSuggestionId: parent.id,
    }),
  },
);

export const deskSuggestionEntity = new schema.Entity<DeskSuggestion>('deskSuggestions', {
  desks: [suggestedDeskEntity],
});

export const accessProfileEntity = new schema.Entity<AccessProfile>(
  'accessProfiles',
  {},
  {
    processStrategy: (entity, parent) => ({
      ...entity,
      userId: parent.id,
    }),
  },
);

export const locationProfileEntity = new schema.Entity<LocationProfile>(
  'locationProfiles',
  {},
  {
    processStrategy: (entity, parent) => ({
      ...entity,
      userId: parent.id,
    }),
  },
);

export const parkingLocationProfileEntity = new schema.Entity<LocationProfile>(
  'parkingLocationProfiles',
  {},
  {
    processStrategy: (entity, parent) => ({
      ...entity,
      userId: parent.id,
    }),
  },
);

export const parkingQuotaProfileEntity = new schema.Entity<ParkingQuotaProfile>(
  'parkingQuotaProfiles',
  {},
  {
    processStrategy: (entity, parent) => ({
      ...entity,
      userId: parent.id,
    }),
  },
);

export const userEntity = new schema.Entity<AppUser>(
  'users',
  {
    effectiveAccessProfile: accessProfileEntity,
    effectiveLocationProfile: locationProfileEntity,
    parkingLocationProfile: parkingLocationProfileEntity,
    parkingQuotaProfile: parkingQuotaProfileEntity,
  },
  {
    processStrategy: (entity) => {
      return {
        id: entity.id,
        initials: entity.initials,
        directoryObjectId: entity.directoryObjectId,
        email: entity.email,
        firstName: entity.firstName,
        lastName: entity.lastName,
        defaultBuildingId: entity.defaultBuildingId,
        isNew: entity.isNew,
        subscriptionId: entity.subscriptionId,
        accessProfileType: entity.accessProfileType,
        locationProfileType: entity.locationProfileType,
        effectiveAccessProfile: entity.effectiveAccessProfile,
        effectiveLocationProfile: entity.effectiveLocationProfile,
        imageHash: entity.imageHash,
        parkingLocationProfile: entity.parkingLocationProfile,
        parkingQuotaProfile: entity.parkingQuotaProfile,
      };
    },
  },
);

export const meetingRoomSuggestionEntity = new schema.Entity<MeetingRoomSuggestion>(
  'suggestionData',
  {},
  {
    idAttribute: (_, parent) => parent.id,
    processStrategy: (entity) => ({
      id: entity.id,
      state: entity.state,
      suggestionId: entity.suggestionData.suggestionId,
      score: entity.suggestionData.score,
      scheduleInformation: entity.scheduleInformation,
      scheduleAvailability: entity.scheduleAvailability,
      sensorAvailability: entity.sensorAvailability,
      numberOfPeople: entity.numberOfPeople,
      sensorStateTimestamp: entity.sensorStateTimestamp,
    }),
  },
);

export const meetingRoomEntity = new schema.Entity<MeetingRoom>(
  'meetingRooms',
  {
    suggestionData: meetingRoomSuggestionEntity,
  },
  {
    processStrategy: (entity) => ({
      id: entity.id,
      activities: entity.activities,
      buildingId: entity.buildingId,
      buildingName: entity.buildingName,
      capacity: entity.capacity,
      displayName: entity.displayName,
      email: entity.email,
      equipment: entity.equipment,
      floorId: entity.floorId,
      floorName: entity.floorName,
      roomPictureHash: entity.roomPictureHash,
      suggestionData: entity.suggestionData ? entity : entity.id,
      type: 'meetingRoom',
    }),
  },
);

export const meetingEntity = new schema.Entity<Meeting>('meetings', {
  meetingRooms: [meetingRoomEntity],
});

export const meetingSuggestionEntity = new schema.Entity<MeetingSuggestion>('suggestions', {
  rooms: [meetingRoomEntity],
});

export const parkingZoneEntity = new schema.Entity<ParkingZone>('parkingZones', {});

export const parkingLotEntity = new schema.Entity<ParkingLot>('parkingLots', {
  parkingZones: [parkingZoneEntity],
});

export const timeZoneEntity = new schema.Entity<TimeZone>(
  'timeZones',
  {},
  {
    idAttribute: 'timeZoneId',
    processStrategy: (entity) => {
      return {
        id: entity.timeZoneId,
        idWithUtcOffset: entity.timeZoneIdWithUtcOffset,
      };
    },
  },
);
